import { createContext, PropsWithChildren, useCallback, useContext, useMemo, useRef } from 'react'
import { useNavigate } from 'react-router'
import { useLocalStorage } from 'react-use'

import { useIsQuickStartEligible } from '@features/quickStart/hooks/useIsQuickStartEligible'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { LocalStorageKeys } from '../../../enums/localStorageKeys'
import { useOrganizationViewUrl } from '../../../hooks/routing/useOrganizationViewUrl'
import { getOrganizationViewUrl } from '../../../utils/routing/getOrganizationViewUrl'
import { reactRoute } from '../../../utils/routing/reactRoute'
import { useOnboardingDashboardRedirect } from '../hooks/useOnboardingDashboardRedirect'
import { useOnboardingStepHistorySync } from '../hooks/useOnboardingStepHistorySync'
import { useOnboardingStepPersistence } from '../hooks/useOnboardingStepPersistence'
import { useOnboardingVariant } from '../hooks/useOnboardingVariant'
import { OnboardingFinalizedStateType, OnboardingSetupStep, OnboardingVariantType } from '../types'
import { navigateToOnboardingStep } from '../utils/navigateToOnboardingStep'

interface FinalizeOnboardingOptions {
  state: OnboardingFinalizedStateType
}

export interface OnboardingSetupContextValue {
  currentStep: OnboardingSetupStep | undefined
  finalizeOnboarding: (options: FinalizeOnboardingOptions) => void
  firstStep: OnboardingSetupStep | undefined
  goToPreviousStep: () => void
  isExtendedQuestionnaire: boolean
  isFetched: boolean
  stepHistory: OnboardingSetupStep[]
  transitionToStep: (step: OnboardingSetupStep) => void
  variant: OnboardingVariantType | undefined
}

export const OnboardingSetupContext = createContext<OnboardingSetupContextValue | null>(null)

export const OnboardingSetupContextProvider = ({ children }: PropsWithChildren) => {
  const { organization } = useUserOrganization()
  const isEligibleForQuickStart = useIsQuickStartEligible()
  const [stepHistory = [], setStepHistory, removeStepHistory] = useLocalStorage<OnboardingSetupStep[]>(
    `${LocalStorageKeys.OnboardingStepHistory}-${organization?.id}`,
  )
  const navigate = useNavigate()
  const { url: baseUrl } = useOrganizationViewUrl(reactRoute.organization.onboarding.getMainRoute())
  const { firstStep, isExtendedQuestionnaire, variant } = useOnboardingVariant()
  const { isFetched, onboardingCurrentStep, updateCurrentStep } = useOnboardingStepPersistence()

  const stepHistoryRef = useRef(stepHistory)
  const { markAsRedirected } = useOnboardingDashboardRedirect(variant)
  useOnboardingStepHistorySync({
    baseUrl,
    firstStep,
    isExtendedQuestionnaire,
    isFetched,
    onboardingCurrentStep,
    setStepHistory,
    stepHistoryRef,
    updateCurrentStep,
  })

  const currentStep = stepHistory.length > 0 ? stepHistory[stepHistory.length - 1] : undefined
  stepHistoryRef.current = stepHistory

  const transitionToStep = useCallback(
    (step: OnboardingSetupStep) => {
      navigateToOnboardingStep({ baseUrl, navigate, step, updateCurrentStep })

      if (stepHistoryRef.current[stepHistoryRef.current.length - 1] !== step) {
        setStepHistory([...stepHistoryRef.current, step])
      }
    },
    [baseUrl, navigate, setStepHistory, updateCurrentStep],
  )

  const goToPreviousStep = useCallback(() => {
    if (stepHistory.length <= 1) {
      return
    }

    const newHistory = stepHistory.slice(0, -1)
    const previousStep = newHistory[newHistory.length - 1]
    setStepHistory(newHistory)
    navigateToOnboardingStep({ baseUrl, navigate, step: previousStep, updateCurrentStep })
  }, [baseUrl, navigate, setStepHistory, stepHistory, updateCurrentStep])

  const finalizeOnboarding = useCallback(
    ({ state }: FinalizeOnboardingOptions) => {
      markAsRedirected()

      const redirectRoute = isEligibleForQuickStart
        ? reactRoute.organization.quickStart.getMainRoute()
        : reactRoute.organization.dashboard.getMainRoute()

      if (organization?.url) {
        navigate(getOrganizationViewUrl(organization.url, redirectRoute))
      } else {
        navigate(redirectRoute)
      }

      updateCurrentStep(state)
      removeStepHistory()
    },
    [isEligibleForQuickStart, markAsRedirected, navigate, organization, removeStepHistory, updateCurrentStep],
  )

  const contextValue: OnboardingSetupContextValue = useMemo(
    () => ({
      currentStep,
      finalizeOnboarding,
      firstStep,
      goToPreviousStep,
      isExtendedQuestionnaire,
      isFetched,
      stepHistory,
      transitionToStep,
      variant,
    }),
    [
      currentStep,
      finalizeOnboarding,
      firstStep,
      goToPreviousStep,
      isExtendedQuestionnaire,
      isFetched,
      stepHistory,
      transitionToStep,
      variant,
    ],
  )

  return <OnboardingSetupContext.Provider value={contextValue}>{children}</OnboardingSetupContext.Provider>
}

export const useOnboardingSetupContext = () => {
  const context = useContext(OnboardingSetupContext)

  if (!context) {
    throw new Error('OnboardingSetupContext is missing in the module!')
  }

  return context
}
